import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { PrismDialogConfig, PrismDialogRef, PrismDialogService } from '@shure/prism-angular-components';
import {
	PrismConfirmDialogComponent,
	PrismConfirmDialogData
} from '@shure/shared/angular/ui/components/dialogs/confirm';
import { PrismInfoDialogComponent, PrismInfoDialogData } from '@shure/shared/angular/ui/components/dialogs/info';

import { IgnoreDialogService } from '../ignore-dialog.service';

const DEFAULT_DIALOG_CONFIG: PrismDialogConfig = {};

export interface DialogLocalStorage {
	doNotShowAgain: string[];
}

@Injectable({ providedIn: 'root' })
export class PrismCommonDialogsService {
	constructor(
		private readonly dialogService: PrismDialogService,
		private readonly ignoreDialogService: IgnoreDialogService
	) {}

	/**
	 * Open confirm dialog
	 */
	public openConfirmDialog(
		data: PrismConfirmDialogData,
		config: PrismDialogConfig = DEFAULT_DIALOG_CONFIG
	): Observable<boolean> {
		const dialogRef = this.openConfirmDialogWithReference(data, config);
		if (!dialogRef) {
			return of(true);
		}

		return dialogRef.afterClosed$.pipe(
			map((result) => {
				if (!result || !result.confirmed) {
					return false;
				}

				this.ignoreDialogService.setIgnoredDialog(result.doNotShowDialog, data.doNotShowDialogId);

				return result.confirmed || false;
			})
		);
	}
	/**
	 * Open confirm dialog as Promise
	 */
	public openConfirmDialogPromise(
		data: PrismConfirmDialogData,
		config: PrismDialogConfig = DEFAULT_DIALOG_CONFIG
	): Promise<boolean> {
		return firstValueFrom(this.openConfirmDialog(data, config));
	}

	/**
	 * Open a info dialog
	 */
	public async openInfoDialog(
		data: PrismInfoDialogData,
		config: PrismDialogConfig = DEFAULT_DIALOG_CONFIG
	): Promise<void> {
		if (data.doNotShowDialogId && this.ignoreDialogService.isDialogIgnored(data.doNotShowDialogId)) {
			return;
		}

		const dialogRef = this.dialogService.openDialog<PrismInfoDialogComponent, PrismInfoDialogData, unknown>(
			PrismInfoDialogComponent,
			{
				...DEFAULT_DIALOG_CONFIG,
				...config,
				data: {
					title: data.title,
					body: typeof data.body === 'string' ? [data.body] : data.body,
					okLabel: data.okLabel,
					showWarningIcon: data.showWarningIcon,
					showBetaIcon: data.showBetaIcon,
					doNotShowDialogId: data.doNotShowDialogId
				}
			}
		);

		const doNotShowDialog = await firstValueFrom(dialogRef.afterClosed$);
		if (doNotShowDialog) {
			this.ignoreDialogService.setIgnoredDialog(doNotShowDialog, data.doNotShowDialogId);
		}
	}

	public openConfirmDialogWithReference(
		data: PrismConfirmDialogData,
		config: PrismDialogConfig = DEFAULT_DIALOG_CONFIG
	): PrismDialogRef<PrismConfirmDialogComponent, { confirmed: boolean; doNotShowDialog: boolean }> | null {
		if (data.doNotShowDialogId && this.ignoreDialogService.isDialogIgnored(data.doNotShowDialogId)) {
			return null;
		}

		return this.dialogService.openDialog<PrismConfirmDialogComponent, PrismConfirmDialogData, unknown>(
			PrismConfirmDialogComponent,
			{
				...config,
				data: {
					title: data.title,
					body: typeof data.body === 'string' ? [data.body] : data.body,
					confirmLabel: data.confirmLabel,
					confirmVariant: data.confirmVariant,
					confirmDisabled: data.confirmDisabled,
					autofocus: data.autofocus || 'confirmButton',
					cancelLabel: data.cancelLabel,
					showIcon: data.showIcon,
					doNotShowDialogId: data.doNotShowDialogId,
					blockElectronMenuAccess: true
				}
			}
		);
	}
}
